import type { NextRouter } from "next/router";
import { parseLoginQueryUid } from "@Front/Utils/loginQueryUidRegex";

/** Narrowing payload for customer auth API calls after candidates / manual choice. */
export type CustomerAuthDisambiguation = {
	customerUid?: string;
	folderUid?: string;
	officeUid?: string;
	loginActor?: "CUSTOMER";
};

/**
 * Optional folder UID from login URL (?folderUid=) to disambiguate customer JWT when the same email exists on several dossiers.
 */
export function extractCustomerLoginFolderUidFromRouter(router: Pick<NextRouter, "isReady" | "query">): string | undefined {
	if (!router.isReady) {
		return undefined;
	}
	return parseLoginQueryUid(router.query["folderUid"]);
}

/**
 * Optional office UID from login URL (?officeUid=) to restrict customer auth to one étude when the same email exists in several offices.
 */
export function extractCustomerLoginOfficeUidFromRouter(router: Pick<NextRouter, "isReady" | "query">): string | undefined {
	if (!router.isReady) {
		return undefined;
	}
	return parseLoginQueryUid(router.query["officeUid"]);
}

/**
 * Optional client UID from login URL (?customerUid=), same as sur les liens dossier / invitation.
 */
export function extractCustomerLoginCustomerUidFromRouter(router: Pick<NextRouter, "isReady" | "query">): string | undefined {
	if (!router.isReady) {
		return undefined;
	}
	return parseLoginQueryUid(router.query["customerUid"]);
}

function buildCustomerLoginAuthScopeFields(
	folderUid: string | undefined,
	officeUid: string | undefined,
	customerUid: string | undefined,
	loginActor: CustomerAuthDisambiguation["loginActor"],
): { folderUid?: string; officeUid?: string; customerUid?: string; loginActor?: string } {
	const out: { folderUid?: string; officeUid?: string; customerUid?: string; loginActor?: string } = {};
	if (folderUid) out.folderUid = folderUid;
	if (officeUid) out.officeUid = officeUid;
	if (customerUid) out.customerUid = customerUid;
	if (loginActor) out.loginActor = loginActor;
	return out;
}

/** Optional folder/office/customer/loginActor for customer auth API calls (URL query + manual disambiguation). */
export function buildCustomerLoginAuthScopePayload(
	loginFolderUid: string | undefined,
	loginOfficeUid: string | undefined,
	disambiguation?: CustomerAuthDisambiguation | null,
	loginCustomerUid?: string | undefined,
): { folderUid?: string; officeUid?: string; customerUid?: string; loginActor?: string } {
	return buildCustomerLoginAuthScopeFields(
		disambiguation?.folderUid ?? loginFolderUid,
		disambiguation?.officeUid ?? loginOfficeUid,
		disambiguation?.customerUid ?? loginCustomerUid,
		disambiguation?.loginActor,
	);
}
